import { KNOWLEDGE_BASE_ENTRY_TYPES } from "@/constants/knowledge-base-entry-types"
import { TransportRequestError } from "@/transport"

import type { AddTestSetRowsTransportRequest } from "@/types_api/backend/transport/add_test_set_rows_transport_request"
import type { CreateTestSetTransportRequest } from "@/types_api/backend/transport/create_test_set_transport_request"
import type { DeleteTestSetTransportRequest } from "@/types_api/backend/transport/delete_test_set_transport_request"
import type { DuplicateTestSetTransportRequest } from "@/types_api/backend/transport/duplicate_test_set_transport_request"
import type { GetMediaPreviewUrlTransportRequest } from "@/types_api/backend/transport/get_media_preview_url_transport_request"
import type { GetMediaUploadUrlTransportRequest } from "@/types_api/backend/transport/get_media_upload_url_transport_request"
import type { SaveTestSetEditorStateTransportRequest } from "@/types_api/backend/transport/save_test_set_editor_state_transport_request"
import type { SaveTestSetEditorStateTransportResponse } from "@/types_api/backend/transport/save_test_set_editor_state_transport_response"
import type { TestSetMetadataRecord as TransportTestSetMetadataRecord } from "@/types_api/backend/transport/test_set_metadata_response"
import type { TestCaseRecord as TransportTestCaseRecord } from "@/types_api/backend/transport/test_set_records_response"
import type { UpdateTestSetTransportRequest } from "@/types_api/backend/transport/update_test_set_transport_request"
import type {
  AssociatedTestCaseAgent,
  AttachedKnowledgeBaseEntry,
  TestCaseRecord,
} from "@/types/api/TestCaseRecord"
import type { TestSetRecord } from "@/types/api/TestSetRecord"
import type { TestSetParameters } from "@/types/backend-contracts"
import { asJsonObjectValueOrNull } from "@/types/json"
import type { ScriptTurn } from "@/types/ScriptTurn"
import {
  ALLOWED_MEDIA_MIME_TYPES,
  type MediaAttachment,
  type TestCaseInput,
} from "@/types/TestCaseInput"
import type { TestSetInput } from "@/types/TestSetInput"

const RESERVED_TEST_SET_ROUTE_IDS = new Set(["create", "new"])

export function isPersistedTestSetId(testSetId?: string | null): boolean {
  return Boolean(testSetId && !RESERVED_TEST_SET_ROUTE_IDS.has(testSetId))
}

export function isDuplicateTestSetNameError(error: unknown): boolean {
  if (error instanceof TransportRequestError) {
    if (error.status === 409) {
      return true
    }
    const haystack = [error.message, error.responseBody, error.detail?.message]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
    return haystack.includes("already exists")
  }
  return String(error instanceof Error ? error.message : error)
    .toLowerCase()
    .includes("already exists")
}

interface CreateTestSetTransportInput {
  testSetInput: TestSetInput
}

interface SaveTestSetEditorStateTransportInput {
  testSetId: string
  metadata: {
    displayName: string
    description?: string
    simulationWrapperType?: string | null
  }
  testCases: SaveTestSetEditorStateTestCaseInput[]
  selectedAgentIds: string[]
}

interface SaveTestSetEditorStateResult {
  success: boolean
  testSet: SaveTestSetEditorStateTransportResponse["test_set"]
  testCases: TestCaseRecord[]
  createdIdMap: SaveTestSetEditorStateTransportResponse["created_id_map"]
}

interface AddTestSetRowsInput {
  testSetId: string
  inputs: TestCaseInput[]
}

interface DeleteTestSetInput {
  testSetId: string
}

interface DuplicateTestSetInput {
  testSetId: string
}

interface UpdateTestSetInput {
  testSetId: string
  displayName?: string
  description?: string | null
  testSetMetadata?: TestSetRecord["test_set_metadata"]
  testSetType?: string | null
  parameters?: TestSetParameters | null
  tags?: string[]
}

interface GetMediaUploadUrlInput {
  testCaseId: string
  filename: string
  mimeType: string
}

export interface GetMediaPreviewUrlInput {
  testCaseId: string
  s3Key?: string | null
}

interface TestCaseInputLike {
  input_str: string
  description?: string | null
  expected_behaviors: string[]
  expected_output_json?: TestCaseInput["expected_output_json"]
  s3_key?: string | null
  s3_bucket?: string | null
  input_type?: string | null
  script_turns?: ScriptTurn[] | null
  simulation_metadata_input?: TestCaseInput["simulation_metadata_input"]
  metric_input?: TestCaseInput["metric_input"]
  connected_knowledge_base_entries?: TestCaseInput["connected_knowledge_base_entries"]
  media_attachments?: TestCaseInput["media_attachments"]
}

interface SaveTestSetEditorStateTestCaseInput extends TestCaseInputLike {
  id: string
  user_notes?: string | null
}

type TransportTestCaseInput = NonNullable<
  CreateTestSetTransportRequest["test_set_input"]["test_cases"]
>[number]

const KNOWLEDGE_BASE_ENTRY_TYPE_SET = new Set<string>(
  KNOWLEDGE_BASE_ENTRY_TYPES,
)

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function normalizeAttachedKnowledgeBaseEntries(
  entries: TransportTestCaseRecord["connected_knowledge_base_entries"],
): AttachedKnowledgeBaseEntry[] {
  if (!Array.isArray(entries)) {
    return []
  }

  return entries.flatMap((entry) => {
    if (
      !isRecord(entry) ||
      typeof entry.id !== "string" ||
      typeof entry.type !== "string" ||
      !KNOWLEDGE_BASE_ENTRY_TYPE_SET.has(entry.type)
    ) {
      return []
    }

    return [
      {
        id: entry.id,
        type: entry.type as AttachedKnowledgeBaseEntry["type"],
        metadata: asJsonObjectValueOrNull(entry.metadata) ?? null,
      },
    ]
  })
}

function normalizeMediaAttachments(
  attachments: TransportTestCaseRecord["media_attachments"],
): MediaAttachment[] {
  if (!Array.isArray(attachments)) {
    return []
  }

  return attachments.flatMap((attachment) => {
    if (
      !isRecord(attachment) ||
      typeof attachment.name !== "string" ||
      typeof attachment.s3_key !== "string" ||
      typeof attachment.s3_bucket !== "string" ||
      typeof attachment.mime_type !== "string" ||
      !ALLOWED_MEDIA_MIME_TYPES.includes(
        attachment.mime_type as MediaAttachment["mime_type"],
      )
    ) {
      return []
    }

    return [
      {
        name: attachment.name,
        s3_key: attachment.s3_key,
        s3_bucket: attachment.s3_bucket,
        mime_type: attachment.mime_type as MediaAttachment["mime_type"],
        description:
          typeof attachment.description === "string"
            ? attachment.description
            : undefined,
      },
    ]
  })
}

function normalizeAssociatedAgents(
  agents: TransportTestCaseRecord["associated_agents"],
): AssociatedTestCaseAgent[] | undefined {
  if (!Array.isArray(agents)) {
    return undefined
  }

  return agents.map((agent) => ({
    ...agent,
    metadata: asJsonObjectValueOrNull(agent.metadata) ?? null,
    workflows: asJsonObjectValueOrNull(agent.workflows) ?? null,
    phone_number: agent.phone_number ?? undefined,
    endpoint: agent.endpoint ?? undefined,
    created_at: agent.created_at ?? undefined,
    last_updated_at: agent.last_updated_at ?? undefined,
  }))
}

export function normalizeTestCaseRecord(
  record: TransportTestCaseRecord,
): TestCaseRecord {
  return {
    ...record,
    expected_behaviors: record.expected_behaviors ?? [],
    expected_output_json: asJsonObjectValueOrNull(record.expected_output_json),
    last_updated_at: record.last_updated_at ?? record.created_at,
    input_type: record.input_type ?? "SCENARIO",
    simulation_metadata_input: asJsonObjectValueOrNull(
      record.simulation_metadata_input,
    ),
    metric_input: asJsonObjectValueOrNull(record.metric_input),
    connected_knowledge_base_entries: normalizeAttachedKnowledgeBaseEntries(
      record.connected_knowledge_base_entries,
    ),
    media_attachments: normalizeMediaAttachments(record.media_attachments),
    associated_agents: normalizeAssociatedAgents(record.associated_agents),
  }
}

function mapKnowledgeBaseEntriesToTransport(
  entries: TestCaseInput["connected_knowledge_base_entries"],
): TransportTestCaseInput["connected_knowledge_base_entries"] {
  return (entries ?? []).map((entry) => ({
    id: entry.id,
    type: entry.type,
    metadata: entry.metadata ? asJsonObjectValueOrNull(entry.metadata) : null,
  }))
}

function mapMediaAttachmentsToTransport(
  attachments: TestCaseInput["media_attachments"],
): TransportTestCaseInput["media_attachments"] {
  return (attachments ?? []).map((attachment) => ({
    name: attachment.name,
    s3_key: attachment.s3_key,
    s3_bucket: attachment.s3_bucket,
    mime_type: attachment.mime_type,
    description: attachment.description,
  }))
}

function normalizeTestSetMetadata(
  metadata: TransportTestSetMetadataRecord["test_set_metadata"],
): TestSetRecord["test_set_metadata"] {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null
  }

  return metadata as TestSetRecord["test_set_metadata"]
}

export function normalizeTestSetMetadataRecord(
  testSet: TransportTestSetMetadataRecord,
): TestSetRecord {
  return {
    ...testSet,
    last_updated_at: testSet.last_updated_at ?? "",
    organization_id: "",
    parameters: (testSet.parameters as TestSetParameters | null) ?? null,
    test_set_metadata: normalizeTestSetMetadata(testSet.test_set_metadata),
    test_case_count: testSet.test_case_count ?? undefined,
    has_expected_behaviors: testSet.has_expected_behaviors ?? false,
    tags: testSet.tags ?? [],
  }
}

function mapTestCaseInputToTransport(
  testCase: TestCaseInputLike,
): TransportTestCaseInput {
  const transportTestCase: TransportTestCaseInput = {
    input_str: testCase.input_str,
    expected_behaviors: testCase.expected_behaviors,
    expected_output_json: testCase.expected_output_json ?? null,
    description: testCase.description ?? null,
    s3_bucket: testCase.s3_bucket ?? null,
    s3_key: testCase.s3_key ?? null,
    input_type: testCase.input_type ?? null,
    // Generated transport type still narrows to string[] | null; the wire
    // format accepts mixed shapes (string | { type, digits } | future dicts),
    // so cast through here until the generated d.ts is regenerated.
    script_turns: (testCase.script_turns ??
      null) as TransportTestCaseInput["script_turns"],
    simulation_metadata_input: testCase.simulation_metadata_input ?? null,
    connected_knowledge_base_entries: mapKnowledgeBaseEntriesToTransport(
      testCase.connected_knowledge_base_entries,
    ),
    media_attachments: mapMediaAttachmentsToTransport(
      testCase.media_attachments,
    ),
  }

  if ("metric_input" in testCase) {
    transportTestCase.metric_input = testCase.metric_input ?? null
  }

  return transportTestCase
}

export function mapCreateTestSetInputToTransport(
  input: CreateTestSetTransportInput,
): CreateTestSetTransportRequest {
  return {
    test_set_input: {
      display_name: input.testSetInput.display_name,
      description: input.testSetInput.description,
      test_set_type: input.testSetInput.test_set_type,
      test_set_metadata: input.testSetInput.test_set_metadata ?? {},
      parameters: input.testSetInput.parameters ?? {},
      test_cases: input.testSetInput.testCases.map(mapTestCaseInputToTransport),
    },
  }
}

export function mapAddTestSetRowsInputToTransport(
  input: AddTestSetRowsInput,
): AddTestSetRowsTransportRequest {
  return {
    test_set_id: input.testSetId,
    inputs: input.inputs.map(mapTestCaseInputToTransport),
  }
}

export function mapDeleteTestSetInputToTransport(
  input: DeleteTestSetInput,
): DeleteTestSetTransportRequest {
  return mapTestSetIdRequest(input)
}

export function mapDuplicateTestSetInputToTransport(
  input: DuplicateTestSetInput,
): DuplicateTestSetTransportRequest {
  return mapTestSetIdRequest(input)
}

function mapTestSetIdRequest(input: { testSetId: string }): {
  test_set_id: string
} {
  return {
    test_set_id: input.testSetId,
  }
}

export function mapUpdateTestSetInputToTransport(
  input: UpdateTestSetInput,
): UpdateTestSetTransportRequest {
  return {
    test_set_id: input.testSetId,
    display_name: input.displayName,
    description: input.description,
    test_set_metadata: input.testSetMetadata ?? undefined,
    test_set_type: input.testSetType,
    parameters: input.parameters,
    tags: input.tags as UpdateTestSetTransportRequest["tags"],
  }
}

export function mapSaveTestSetEditorStateInputToTransport(
  input: SaveTestSetEditorStateTransportInput,
): SaveTestSetEditorStateTransportRequest {
  return {
    test_set_id: input.testSetId,
    metadata: {
      display_name: input.metadata.displayName,
      description: input.metadata.description ?? null,
      simulation_wrapper_type: input.metadata.simulationWrapperType ?? null,
    },
    test_cases: input.testCases.map((testCase) => ({
      ...mapTestCaseInputToTransport(testCase),
      id: testCase.id,
      user_notes: testCase.user_notes ?? null,
    })),
    selected_agent_ids: input.selectedAgentIds,
  }
}

export function mapSaveTestSetEditorStateResponse(
  response: SaveTestSetEditorStateTransportResponse,
): SaveTestSetEditorStateResult {
  return {
    success: response.success,
    testSet: response.test_set,
    testCases: response.test_cases.map(normalizeTestCaseRecord),
    createdIdMap: response.created_id_map,
  }
}

export function mapMediaUploadUrlInputToTransport(
  input: GetMediaUploadUrlInput,
): GetMediaUploadUrlTransportRequest {
  return {
    test_case_id: input.testCaseId,
    filename: input.filename,
    mime_type: input.mimeType,
  }
}

export function mapMediaPreviewUrlInputToTransport(
  input: GetMediaPreviewUrlInput,
): GetMediaPreviewUrlTransportRequest {
  return {
    test_case_id: input.testCaseId,
    s3_key: input.s3Key ?? null,
  }
}
